import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { AttachmentsAndWaiversRequest } from './models/attachments-and-waivers-request';
import { AttachmentsAndWaiversResponse } from './models/attachments-and-waivers-response';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { ActivatedRoute, Router } from '@angular/router';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { Observable, takeUntil, tap } from 'rxjs';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { DropdownOption } from '../../../../core/models/dropdown-option';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { AttachmentsAndWaiversHelperMessages } from './constants/attachments-and-waivers-messages';
import { FieldDeselector } from '../../../../core/models/field-deselector';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { FormControlService } from '../../../../core/services/form-control/form-control.service';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { AttachmentGridComponent } from '../shared/components/attachment/attachment-grid/attachment-grid.component';

@Component({
    selector: 'app-attachments-and-waivers',
    templateUrl: './attachments-and-waivers.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        ApplicationFormFieldComponent,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        RegexInputDirective,
        AttachmentGridComponent,
    ],
})
export class AttachmentsAndWaiversComponent
    extends ApplicationPageBaseComponent<
        AttachmentsAndWaiversRequest,
        AttachmentsAndWaiversResponse,
        AttachmentsAndWaiversResponse
    >
    implements OnInit, OnDestroy, AfterViewInit, BlockNavigationIfChange
{
    regExPattern = RegExPattern;
    messages: typeof AttachmentsAndWaiversHelperMessages =
        AttachmentsAndWaiversHelperMessages;
    yesNoOptions: DropdownOption[] = [];
    @ViewChild('commentBox') commentBox!: ElementRef;

    private readonly dependentFormControlsForResetOptions: FieldDeselector<AttachmentsAndWaiversResponse> =
        {
            anyAdditionalAttachments: {
                dependentFields: ['additionalAttachmentNotes'],
            },
        };

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly router: Router,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly formDeselectService: FormControlService,
        private readonly directEntryService: DirectEntryService,
        private readonly dropdownService: DropdownService,
    ) {
        super(route, router, applicationPageFacadeService);
    }

    showAdditionalAttachmentNotes() {
        return this.form.controls.anyAdditionalAttachments.value;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.commentBox.nativeElement.focus();
        });
    }

    override form: FormGroup<FormGroupType<AttachmentsAndWaiversResponse>> =
        new FormGroup<FormGroupType<AttachmentsAndWaiversResponse>>({
            comments: new FormControl(undefined, [
                Validators.maxLength(1000),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            anyAdditionalAttachments: new FormControl(
                undefined,
                Validators.required,
            ),
            additionalAttachmentNotes: new FormControl(undefined, [
                Validators.maxLength(1000),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
        });

    private loadDropdownData() {
        this.dropdownService
            .getYesNoOptions()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: DropdownOption[]) => {
                    this.yesNoOptions = options;
                }),
            )
            .subscribe();
    }

    protected override bindForm(response: AttachmentsAndWaiversResponse): void {
        this.form.patchValue(response);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<AttachmentsAndWaiversResponse> {
        return this.directEntryService.getAttachmentsAndWaivers(applicationId);
    }

    protected override saveFormDataToApi(
        request: AttachmentsAndWaiversRequest,
    ): Observable<StepValidationResult> {
        return this.directEntryService.saveAttachmentsAndWaivers(request);
    }

    protected override generateRequestObject(): AttachmentsAndWaiversRequest {
        const formValue = this.form
            .value as Required<AttachmentsAndWaiversRequest>;

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }

    protected override postFormBindingActions(): void {
        this.setupFormDeselectors();
    }

    private setupFormDeselectors(): void {
        this.formDeselectService.setupFormDeselectorsHandler(
            this.form as FormGroup<
                FormGroupType<AttachmentsAndWaiversResponse>
            >,
            this.dependentFormControlsForResetOptions,
            this.destroy$,
        );
    }
}
